import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { registerUser, getGoogleAuthUrl, resendVerification } from '../api/portalApi';
import { track } from '../utils/analytics';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

const RESEND_COOLDOWN = 60;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const handleResend = async () => {
    if (resendCooldown > 0 || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await resendVerification(email);
      track('resend-verification');
      setResendStatus('sent');
      setResendCooldown(RESEND_COOLDOWN);
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setResendStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await registerUser({ name: name.trim(), email: email.trim(), password });
      track('register', { method: 'email' });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <main className="investor-login-shell">
        <div className="investor-login-logo">
          <img src="/logos/nomadme_sourcing_trading_mark.png" alt="nomadme" />
        </div>
        <p className="investor-login-brand">NomadMe</p>
        <section className="investor-login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✉️</div>
          <h2 style={{ color: '#f1f5f9', marginBottom: 12 }}>{t('register.checkEmail')}</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 24 }}>
            {t('register.verificationSentTo')} <strong style={{ color: '#c8a06a' }}>{email}</strong>.{' '}
            {t('register.verificationSentClick')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendStatus === 'sending'}
              style={{
                background: 'none',
                border: `1px solid ${resendCooldown > 0 ? 'rgba(200,160,106,0.25)' : 'rgba(200,160,106,0.55)'}`,
                borderRadius: 8,
                color: resendCooldown > 0 ? '#64748b' : '#c8a06a',
                cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
                padding: '7px 16px',
                transition: 'all 0.2s',
              }}
            >
              {resendStatus === 'sending'
                ? t('register.resendSending')
                : resendCooldown > 0
                ? t('register.resendCooldown', { seconds: resendCooldown })
                : resendStatus === 'sent'
                ? t('register.resendSent')
                : t('register.resendBtn')}
            </button>
            {resendStatus === 'error' && (
              <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: 0 }}>{t('register.resendError')}</p>
            )}
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
            >
              {t('register.backToSignIn')}
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="investor-login-shell">
      <div className="investor-login-topbar">
        <LanguageSwitcher variant="ghost" />
      </div>

      <div className="investor-login-logo">
        <img src="/logos/nomadme_sourcing_trading_mark.png" alt="nomadme" />
      </div>
      <p className="investor-login-brand">NomadMe</p>

      <section className="investor-login-card">
        <Link to="/" className="auth-back-link">{t('register.backToHome')}</Link>
        <h2>{t('register.createAccount')}</h2>
        <p className="investor-login-subtitle">{t('register.joinCommunity')}</p>

        <a
          href={getGoogleAuthUrl()}
          className="auth-google-btn"
          onClick={() => track('register-click', { method: 'google' })}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          {t('register.continueGoogle')}
        </a>

        <div className="auth-divider"><span>{t('register.dividerOr')}</span></div>

        <form className="investor-login-form" onSubmit={handleSubmit}>
          <div className="investor-login-field">
            <label htmlFor="name">{t('register.fullName')}</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('register.fullNamePlaceholder')}
              required
              autoComplete="name"
            />
          </div>

          <div className="investor-login-field">
            <label htmlFor="email">{t('register.emailAddress')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="investor-login-field">
            <label htmlFor="password">{t('register.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('register.passwordPlaceholder')}
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>

          {error && <p className="investor-login-error">{error}</p>}

          <button type="submit" disabled={isLoading} className="investor-login-btn">
            {isLoading ? t('register.creating') : t('register.submit')}
          </button>
        </form>

        <div className="investor-login-footer">
          {t('register.hasAccount')} <Link to="/login">{t('register.signIn')}</Link>
        </div>
      </section>
    </main>
  );
};

export default RegisterPage;
